import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import useBadge from './useBadge'
import { capitalize } from '../../utils'

export interface BadgeClasses {
  /** Class name applied to the root element. */
  root: string
  /** Class name applied to the badge `span` element. */
  badge: string
  /** Class name applied to the badge `span` element if `variant="dot"`. */
  dot: string
  /** Class name applied to the badge `span` element if `variant="standard"`. */
  standard: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'right' }}`. */
  anchorOriginTopRight: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'right' }}`. */
  anchorOriginBottomRight: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'left' }}`. */
  anchorOriginTopLeft: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'left' }}`. */
  anchorOriginBottomLeft: string
  /** State class applied to the badge `span` element if `invisible={true}`. */
  invisible: string
  /** Styles applied to the badge `span` element if `color="primary"`. */
  colorPrimary: string
  /** Styles applied to the badge `span` element if `color="secondary"`. */
  colorSecondary: string
  /** Styles applied to the badge `span` element if `color="error"`. */
  colorError: string
  /** Styles applied to the badge `span` element if `color="info"`. */
  colorInfo: string
  /** Styles applied to the badge `span` element if `color="success"`. */
  colorSuccess: string
  /** Styles applied to the badge `span` element if `color="warning"`. */
  colorWarning: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'right' }} overlap="rectangular"`. */
  anchorOriginTopRightRectangular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'right' }} overlap="rectangular"`. */
  anchorOriginBottomRightRectangular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'left' }} overlap="rectangular"`. */
  anchorOriginTopLeftRectangular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'left' }} overlap="rectangular"`. */
  anchorOriginBottomLeftRectangular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'right' }} overlap="circular"`. */
  anchorOriginTopRightCircular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'right' }} overlap="circular"`. */
  anchorOriginBottomRightCircular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'top', 'left' }} overlap="circular"`. */
  anchorOriginTopLeftCircular: string
  /** Class name applied to the badge `span` element if `anchorOrigin={{ 'bottom', 'left' }} overlap="circular"`. */
  anchorOriginBottomLeftCircular: string
  /** Class name applied to the badge `span` element if `overlap="rectangular"`. */
  overlapRectangular: string
  /** Class name applied to the badge `span` element if `overlap="circular"`. */
  overlapCircular: string
}

export interface BadgeOrigin {
  vertical: 'top' | 'bottom'
  horizontal: 'left' | 'right'
}

export interface BadgeProps
  extends Partial<React.HTMLAttributes<HTMLSpanElement>> {
  /**
   * The content rendered within the badge.
   */
  badgeContent?: React.ReactNode
  /**
   * The badge will be added relative to this node.
   */
  children?: React.ReactNode
  /**
   * If `true`, the badge is invisible.
   * @default false
   */
  invisible?: boolean
  /**
   * Max count to show.
   * @default 99
   */
  max?: number
  /**
   * Controls whether the badge is hidden when `badgeContent` is zero.
   * @default false
   */
  showZero?: boolean
  /**
   * The anchor of the badge.
   * @default {
   *   vertical: 'top',
   *   horizontal: 'right',
   * }
   */
  anchorOrigin?: BadgeOrigin
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<BadgeClasses>
  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default span
   */
  Component?: React.ElementType
  /**
   * Wrapped shape the badge should overlap.
   * @default 'rectangular'
   */
  overlap?: 'rectangular' | 'circular'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * @default 'standard'
   */
  variant?: 'standard' | 'dot'
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge(props, ref) {
    const {
      anchorOrigin = {
        vertical: 'top',
        horizontal: 'right',
      },
      className,
      classes,
      Component = 'span',
      overlap = 'rectangular',
      color = 'primary',
      invisible: invisibleProp = false,
      max: maxProps = 99,
      badgeContent: badgeContentProp,
      showZero = false,
      variant = 'standard',
      size = 'medium',
      children,
      sx,
      ...other
    } = props

    const { invisible, displayValue } = useBadge({
      badgeContent: badgeContentProp,
      invisible: invisibleProp,
      max: maxProps,
      showZero,
      variant,
    })
    const _rootClasses = clsx(style.root, classes?.root, className, sxFunc(sx))
    const _badgeClasses = clsx(
      style.badge,
      classes?.badge,
      invisible && [classes?.invisible, style.invisible],
      color && [
        style[`color-${color}`],
        // @ts-ignore
        classes?.[`color${capitalize(color)}`],
      ],
      variant === 'dot'
        ? badgeContentProp
          ? typeof badgeContentProp === 'number'
            ? style['variant-dotWithContentNumber']
            : style['variant-dotWithContentIcon']
          : [style['variant-dot'], classes?.dot]
        : classes?.standard,
      style[`overlap-${overlap}`],
      style[`size--${size}`],
      variant === 'dot' && !children ? style['variant-dotStatus'] : '',
      //@ts-ignore
      classes?.[`overlap${capitalize(overlap)}`],
      style[`anchorOrigin-${anchorOrigin.vertical}-${anchorOrigin.horizontal}`],
      // @ts-ignore
      classes?.[
        `anchorOrigin${capitalize(anchorOrigin.vertical)}${capitalize(
          anchorOrigin.horizontal
        )}`
      ]
    )

    return (
      <Component {...other} className={_rootClasses} ref={ref}>
        {children}
        <span className={_badgeClasses}>{displayValue}</span>
      </Component>
    )
  }
)

/*
<BadgeUnstyled
        invisible={invisibleProp}
        badgeContent={displayValue}
        showZero={showZero}
        max={max}
        {...other}
        className={clsx(rootSlotProps?.className, classes.root, className)}
        slotProps={{
          root: {
            ...rootSlotProps,
            ...(shouldSpreadAdditionalProps(RootSlot) && {
              as: component,
              ownerState: {
                ...rootSlotProps?.ownerState,
                anchorOrigin,
                color,
                overlap,
                variant,
              },
            }),
          },
          badge: {
            ...badgeSlotProps,
            className: clsx(classes.badge, badgeSlotProps?.className),
            ...(shouldSpreadAdditionalProps(BadgeSlot) && {
              ownerState: {
                ...badgeSlotProps?.ownerState,
                anchorOrigin,
                color,
                overlap,
                variant,
              },
            }),
          },
        }}
        ref={ref}
      />
*/
